
'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { Button } from '@/components/ui/button'
import { 
  Menu, 
  X, 
  ChevronDown, 
  Phone, 
  Mail, 
  Clock,
  User,
  FileText,
  Building,
  Heart,
  Scale,
  MessageCircle
} from 'lucide-react'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu"
import { cn } from "@/lib/utils"

export default function Header() {
  const [isOpen, setIsOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0)
    }
    
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  const toggleMenu = () => {
    setIsOpen(!isOpen)
  }

  const immigrationServices = [
    { title: 'Employer Sponsored Visas', href: '/services/employer-sponsored', icon: Building },
    { title: 'Australian Citizenship', href: '/services/australian-citizenship', icon: User },
    { title: 'Partner & Parent Visas', href: '/services/partner-parent-visa', icon: Heart },
    { title: 'Working Holiday Visa', href: '/services/working-holiday-visa', icon: User },
    { title: 'Visitor Visas', href: '/services/visitor-visas', icon: User },
    { title: 'Business & Investment Visa', href: '/services/business-investment-visa', icon: Building },
    { title: 'Student Visa', href: '/services/student-visa', icon: User },
    { title: 'Resident Return Visa', href: '/services/resident-return-visa', icon: User },
    { title: 'Visa Appeals & Interventions', href: '/services/visa-appeals', icon: Scale },
  ]

  const aboutUsItems = [
    { title: 'Our Team', href: '/about/team', icon: User },
    { title: 'Our Achievements', href: '/about/achievements', icon: FileText },
    { title: 'Testimonials', href: '/about/testimonials', icon: Heart },
    { title: 'News & Blog', href: '/blog', icon: FileText },
  ]

  return (
    <>
      {/* Top Contact Bar */}
      <div className="bg-blue-900 text-white py-2 text-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center sm:items-center justify-between gap-2 sm:gap-4">
            <div className="flex flex-col sm:flex-row items-center sm:items-center gap-2 sm:gap-6 w-full sm:w-auto text-center sm:text-left">
              <div className="flex items-center gap-2 min-w-0">
                <Phone className="w-4 h-4 flex-shrink-0" />
                <a href="tel:+61483907326" className="hover:text-blue-200 transition-colors truncate">
                  +61 483 907 326
                </a>
              </div>
              <div className="flex items-center gap-2 min-w-0 sm:hidden lg:flex">
                <MessageCircle className="w-4 h-4 flex-shrink-0" />
                <a href="sms:0483907326" className="hover:text-blue-200 transition-colors truncate">
                  0483 907 326
                </a>
              </div>
              <div className="flex items-center gap-2 min-w-0">
                <Mail className="w-4 h-4 flex-shrink-0" />
                <a href="mailto:info@visatec.com.au" className="hover:text-blue-200 transition-colors truncate">
                  info@visatec.com.au
                </a>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs sm:text-sm">
              <Clock className="w-4 h-4 flex-shrink-0" />
              <span className="whitespace-nowrap">Mon-Fri: 9:00 AM - 6:00 PM</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className={cn(
        "sticky top-0 z-50 transition-all duration-300",
        isScrolled ? "bg-white/95 backdrop-blur-sm shadow-md" : "bg-white"
      )}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link href="/" className="flex items-center">
              <div className="relative w-48 h-12">
                <Image
                  src="https://cdn.abacus.ai/images/74600ebe-f729-4c6a-bebd-0217a333d059.png"
                  alt="Visatec Legal Logo"
                  fill
                  className="object-contain"
                  priority
                />
              </div>
            </Link>

            {/* Desktop Navigation */}
            <NavigationMenu className="hidden lg:flex">
              <NavigationMenuList>
                <NavigationMenuItem>
                  <Link href="/" legacyBehavior passHref>
                    <NavigationMenuLink className={cn(
                      "group inline-flex h-10 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50"
                    )}>
                      Home
                    </NavigationMenuLink>
                  </Link>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger>About Us</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                      {aboutUsItems?.map((item) => (
                        <li key={item?.title}>
                          <NavigationMenuLink asChild>
                            <Link
                              href={item?.href || '#'}
                              className={cn(
                                "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                              )}
                            >
                              <div className="flex items-center gap-2">
                                <item.icon className="w-4 h-4" />
                                <div className="text-sm font-medium leading-none">{item?.title}</div>
                              </div>
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <NavigationMenuTrigger>Services</NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-3 p-4 md:w-[500px] md:grid-cols-2 lg:w-[800px] lg:grid-cols-3">
                      <li className="row-span-3">
                        <NavigationMenuLink asChild>
                          <Link
                            className="flex h-full w-full select-none flex-col justify-end rounded-md bg-gradient-to-b from-blue-500/20 to-blue-700/20 p-6 no-underline outline-none focus:shadow-md"
                            href="/services"
                          >
                            <div className="mb-2 mt-4 text-lg font-medium">
                              Our Services
                            </div>
                            <p className="text-sm leading-tight text-muted-foreground">
                              Professional legal services with over 20 years of experience
                            </p>
                          </Link>
                        </NavigationMenuLink>
                      </li>
                      {immigrationServices?.map((service) => (
                        <li key={service?.title}>
                          <NavigationMenuLink asChild>
                            <Link
                              href={service?.href || '#'}
                              className={cn(
                                "block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                              )}
                            >
                              <div className="flex items-center gap-2">
                                <service.icon className="w-4 h-4" />
                                <div className="text-sm font-medium leading-none">{service?.title}</div>
                              </div>
                            </Link>
                          </NavigationMenuLink>
                        </li>
                      ))}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link href="/get-started" legacyBehavior passHref>
                    <NavigationMenuLink className={cn(
                      "group inline-flex h-10 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50"
                    )}>
                      Get Started Online
                    </NavigationMenuLink>
                  </Link>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link href="/client-portal" legacyBehavior passHref>
                    <NavigationMenuLink className={cn(
                      "group inline-flex h-10 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50"
                    )}>
                      Client Portal
                    </NavigationMenuLink>
                  </Link>
                </NavigationMenuItem>

                <NavigationMenuItem>
                  <Link href="/contact" legacyBehavior passHref>
                    <NavigationMenuLink className={cn(
                      "group inline-flex h-10 w-max items-center justify-center rounded-md bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground focus:outline-none disabled:pointer-events-none disabled:opacity-50 data-[active]:bg-accent/50 data-[state=open]:bg-accent/50"
                    )}>
                      Contact
                    </NavigationMenuLink>
                  </Link>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            {/* CTA Button & Mobile Menu */}
            <div className="flex items-center gap-4">
              <Button asChild className="hidden sm:inline-flex bg-blue-600 hover:bg-blue-700">
                <Link href="/contact">Contact Us</Link>
              </Button>
              
              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="sm"
                className="lg:hidden"
                onClick={toggleMenu}
                aria-label="Toggle menu"
              >
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {isOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg">
            <div className="px-4 py-2 space-y-1 max-h-96 overflow-y-auto">
              <Link href="/" className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md" onClick={toggleMenu}>
                Home
              </Link>
              
              <div className="space-y-1">
                <div className="px-3 py-2 text-base font-medium text-gray-900">About Us</div>
                {aboutUsItems?.map((item) => (
                  <Link key={item?.title} href={item?.href || '#'} className="block pl-6 pr-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-md" onClick={toggleMenu}>
                    {item?.title}
                  </Link>
                ))}
              </div>

              <div className="space-y-1">
                <div className="px-3 py-2 text-base font-medium text-gray-900">Services</div>
                {immigrationServices?.slice(0, 6)?.map((service) => (
                  <Link key={service?.title} href={service?.href || '#'} className="block pl-6 pr-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-gray-50 rounded-md" onClick={toggleMenu}>
                    {service?.title}
                  </Link>
                ))}
              </div>

              <Link href="/get-started" className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md" onClick={toggleMenu}>
                Get Started Online
              </Link>
              <Link href="/client-portal" className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md" onClick={toggleMenu}>
                Client Portal
              </Link>
              <Link href="/contact" className="block px-3 py-2 text-base font-medium text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md" onClick={toggleMenu}>
                Contact
              </Link>
              
              <div className="pt-4 pb-2">
                <Button asChild className="w-full bg-blue-600 hover:bg-blue-700" onClick={toggleMenu}>
                  <Link href="/contact">Contact Us</Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  )
}
